// Conversation I/O primitives + shared types — the runtime-agnostic leaf of
// the conversation domain, used by both runtimes: the Electron
// ConversationStore (src/storage/conversations.ts) and the cloud UserDO's
// conversation store (workers/cloud).
//
// Persistence is reached through the ConversationStorage port (Cloud Arch §4):
// the port moves OPAQUE strings, so this module owns all parsing — JSONL message
// decoding and the JSON parse contract these stores share with the former
// `readJSON` helper (missing/empty/corrupt → null; a real I/O error still throws
// upstream, before parse).

import { parseJsonlLine } from './jsonl.js';
import { GENERAL_PROJECT } from './general.js';
import type { StoredAttachment } from './chatAttachments.js';

/**
 * Parse a raw JSON blob with the exact contract the conversation stores relied
 * on when they read through `readJSON`: `null` (never-written), empty/whitespace,
 * or corrupt JSON all collapse to `null`; a valid document parses. Genuine I/O
 * failures throw at the port BEFORE this is reached (readText rethrows non-ENOENT).
 */
export function parseJson<T>(raw: string | null): T | null {
  if (raw === null) return null;
  if (!raw.trim()) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

// Types

/** Stored message — supports both plain text and structured content blocks. */
export interface StoredMessage {
  role: 'user' | 'assistant';
  /** Plain text OR structured ContentBlock[] (tool_use, tool_result, text). */
  content: string | StoredContentBlock[];
  timestamp: string;
  /**
   * Provenance discriminator (I-1, 2026-09-13). The Anthropic protocol forces
   * tool results into role:'user' messages, which conflates "the operator
   * spoke" with "protocol plumbing" — the root cause of the 2026-07 phantom-
   * consent bug (commit 7a79407 cut persistence entirely to kill it; this
   * field restores persistence WITH the distinction the schema always needed).
   * - 'operator': a real human message.
   * - 'tool': a tool-results carrier.
   * - 'checkpoint': a summary of the messages before it (ADR-0002 amendment
   *   2026-10-12) — `content` is the summary text, `covers` how many messages
   *   from the start of the log it summarizes. What a model is sent is chosen
   *   by `messagesSinceCheckpoint` (`conversationCheckpoint.ts`).
   *
   * Every reader goes through {@link roleLabel}, never `role` alone.
   */
  kind?: 'operator' | 'tool' | 'checkpoint' | undefined;
  /** On a checkpoint: how many messages from the start of the log its summary covers. */
  covers?: number | undefined;
  /** On an assistant message: the provider and model that wrote it — a conversation can move between them. */
  provider?: string | undefined;
  model?: string | undefined;
  /** On an operator message: the files attached to it (`chatAttachments.ts`); `content` is only what was typed. */
  attachments?: StoredAttachment[] | undefined;
  /**
   * The project this message's work happened in: the conversation's active
   * project when the store appended it. It is the one record of where each
   * part of a conversation belongs — search indexing, rebuilds and project-
   * scoped search all read it (see {@link projectOfMessage}). The store sets
   * it; callers append messages without it.
   */
  project: string;
}

/** A message as callers hand it to a store: the store labels it with the project. */
export type NewStoredMessage = Omit<StoredMessage, 'project'>;

/** Who a message is from, as every renderer, search index and summary names it. */
export type MessageLabel = 'operator' | 'assistant' | 'tool' | 'checkpoint';

/**
 * Who a message is from — the one rule. The API puts tool results in user-role
 * messages, so `role` alone would show tool output as the operator speaking.
 * A user message is a tool carrier when it is labelled so or carries any
 * tool_result block: the blocks cover messages that never had a label (the
 * repair's synthesized results, the agent loop's in-memory messages), and a
 * message mixing a result with text is labelled tool, never operator.
 */
export function roleLabel(m: {
  role: 'user' | 'assistant';
  kind?: StoredMessage['kind'];
  content: unknown;
}): MessageLabel {
  if (m.role === 'assistant') return 'assistant';
  if (m.kind === 'checkpoint') return 'checkpoint';
  if (m.kind === 'tool') return 'tool';
  const hasResult =
    Array.isArray(m.content) && m.content.some((b: { type?: unknown } | null) => b?.type === 'tool_result');
  return hasResult ? 'tool' : 'operator';
}

/**
 * The project a stored message belongs to when searching: its own label while
 * that project exists, General once it has been deleted. Applied at read time,
 * so deleting a project never rewrites stored messages and restoring it brings
 * its messages straight back. Desktop FTS search applies the same rule in SQL
 * (`ConversationsFts.search`).
 */
export function projectOfMessage(label: string, liveProjects: ReadonlySet<string>): string {
  return liveProjects.has(label) ? label : GENERAL_PROJECT;
}

/**
 * Scope for a project-limited conversation search. The live project list rides
 * along so the store can apply {@link projectOfMessage}; requiring it here means
 * no caller can scope a search without it.
 */
export interface ConversationSearchScope {
  project: string;
  liveProjects: readonly string[];
}

/** Matches the agent's ContentBlock type — stored as-is in JSONL. */
export type StoredContentBlock =
  | { type: 'text'; text: string }
  | { type: 'thinking'; thinking: string; signature: string }
  | { type: 'redacted_thinking'; data: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; tool_use_id: string; content: string; is_error?: boolean | undefined };

/** Parse raw messages.jsonl content into structured messages. Corrupt lines skipped. */
export function parseStoredMessages(raw: string | null): StoredMessage[] {
  if (!raw) return [];
  const messages: StoredMessage[] = [];
  for (const line of raw.split('\n')) {
    const message = parseJsonlLine<StoredMessage>(line); // a torn line is skipped
    if (message) messages.push(message);
  }
  return messages;
}

/** One page of a conversation's log, as the chat view shows it. */
export interface MessagePage {
  messages: StoredMessage[];
  /** The log index this page starts at — pass it as `before` for the page above it; null at the start of the log. */
  earlier: number | null;
}

/**
 * The `size` messages before log index `before` (absent: the end of the log).
 * A page never starts on a tool-results message: results attach to the tool
 * call in the message before them, so the start moves back to keep the pair
 * on one page and every page decodes on its own. Indexes are stable because
 * the log is append-only (`ConversationStorage`).
 */
export function pageOfMessages(all: readonly StoredMessage[], size: number, before?: number): MessagePage {
  const end = Math.min(before ?? all.length, all.length);
  let start = Math.max(0, end - Math.max(1, size));
  for (let m = all[start]; start > 0 && m && roleLabel(m) === 'tool'; m = all[start]) start--;
  return { messages: all.slice(start, end), earlier: start > 0 ? start : null };
}

/** A message's searchable text: every block type (text, thinking, tool_use, tool_result) and its attachments' names. */
export function extractSearchableText(message: Pick<StoredMessage, 'content' | 'attachments'>): string {
  const names = (message.attachments ?? []).map((a) => `[attached] ${a.name}`);
  return [contentText(message.content), ...names].filter(Boolean).join(' ');
}

function contentText(content: string | StoredContentBlock[]): string {
  if (typeof content === 'string') return content;
  const parts: string[] = [];
  for (const block of content) {
    if (block.type === 'text') {
      parts.push(block.text);
    } else if (block.type === 'thinking') {
      parts.push(`[thinking] ${block.thinking}`);
    } else if (block.type === 'tool_use') {
      let inputStr = '';
      try {
        inputStr = typeof block.input === 'string' ? block.input : JSON.stringify(block.input ?? {});
      } catch {
        /* circular / non-serializable input — skip the payload, still tag the tool */
      }
      parts.push(`[tool:${block.name}] ${inputStr}`);
    } else if (block.type === 'tool_result') {
      parts.push(`[tool_result] ${block.content}`);
    }
    // redacted_thinking has no readable content by design — skip it.
  }
  return parts.join(' ');
}

// Metadata types

export interface ConversationMeta {
  id: string;
  title: string;
  provider: string;
  model: string;
  /** The conversation's project — General (`../general.ts`) until it's switched. */
  activeProject: string;
  messageCount: number;
  /** Cumulative tokens across all turns. */
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheWriteTokens: number;
  totalCost: number;
  /**
   * Last turn's MEASURED prompt tokens (provider ground truth: input +
   * cache-read + cache-write). Replace-with-latest, not cumulative. The single
   * basis for context fill % (meter + agent block) and the context-fill warning.
   * Absent before the first measured turn and after a summarize clears it.
   */
  lastRequestPromptTokens?: number;
  /** Skills loaded via skill_load — persisted so they survive summarization. */
  loadedSkills?: string[];
  /** Extended context window (e.g. 1M on Opus/Sonnet). */
  useExtendedContext?: boolean;
  /**
   * Desktop subscription path only: the Claude Code transcript held in this
   * conversation's folder (a rebuildable cache — the store stays the memory).
   * `coveredCount` is `messageCount` after the transcript's last successful
   * turn; the next turn may resume it only if exactly one message (the new
   * operator message) was stored since. See src/agent/claudeCode/transcript.ts.
   */
  cliTranscript?: {
    sessionId: string;
    model: string;
    useExtendedContext: boolean;
    coveredCount: number;
    /** `checkpoints` when the session was built — a newer checkpoint means it no longer matches. */
    checkpoints: number;
  };
  /** How many checkpoints the log holds (`writeCheckpoint` counts them, as `messageCount` counts messages). */
  checkpoints?: number;
  /**
   * Projects this conversation has looked into besides the active one —
   * read-only reference. A project joins when the agent first reads it (or
   * it was the active project before a switch); bash and quality tools may run
   * in these. Cleared by summarize. See src/core/projectScope.ts.
   */
  projectsInView?: string[];
  /**
   * The project the current session's system prompt was built for. Switching
   * the active project does not rebuild the prompt (no context is wiped or
   * re-sent); it resets to the active project when a session starts fresh
   * (new conversation, summarize, a rebuilt Claude Code transcript).
   */
  contextProject?: string;
  status?: 'active' | 'completed';
  /** ISO timestamp when the conversation was marked completed. */
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationSummary {
  id: string;
  title: string;
  provider: string;
  model: string;
  activeProject: string;
  messageCount: number;
  /** Lifecycle: `active` (default) or `completed` (shown in "Recent"). */
  status: 'active' | 'completed';
  /** ISO timestamp when marked completed — null if active. */
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
